import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Check, Lock, Star, Trophy, BookOpen, Target, MessageCircle, RotateCcw } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Progress } from "@/components/ui/progress";
import { DownloadCard, SavedMark } from "@/components/DownloadCard";
import { savedLessonKeys } from "@/lib/offlineContent";

const NODE = {
  completed: "bg-[hsl(var(--primary))] text-white shadow-lg shadow-emerald-500/25 ring-4 ring-[hsl(var(--primary-light))]",
  current: "bg-[hsl(var(--secondary))] text-white ring-8 ring-[hsl(var(--secondary-light))] scale-110 shadow-xl animate-bob",
  locked: "bg-slate-100 text-slate-400 border-2 border-slate-200 cursor-not-allowed",
};
const OFFSETS = ["translate-x-0", "translate-x-14", "translate-x-20", "translate-x-10", "-translate-x-8", "-translate-x-16"];

const TopicNode = ({ t, i, active, saved, onClick }) => (
  <div className={`flex flex-col items-center ${OFFSETS[i % OFFSETS.length]}`}>
    <button
      data-testid={`topic-node-${t.id}`}
      disabled={t.status === "locked"}
      onClick={onClick}
      data-saved={saved ? "true" : "false"}
      className={`relative h-20 w-20 rounded-full grid place-items-center transition-transform active:scale-95 ${NODE[t.status]} ${active ? "outline outline-4 outline-offset-4 outline-slate-900/80" : ""}`}
    >
      {t.status === "completed" ? <Check size={32} strokeWidth={3} /> : t.status === "locked" ? <Lock size={26} /> : t.id === "final" ? <Trophy size={30} /> : <Star size={30} className="fill-current" />}
      {saved && <SavedMark />}
    </button>
    <p className={`mt-3 text-sm font-bold text-center max-w-[150px] ${t.status === "locked" ? "text-slate-400" : "text-slate-800"}`}>{t.title}</p>
    {t.kind && <span data-testid={`topic-kind-${t.id}`} className="mt-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[hsl(var(--accent-blue-light))] text-[hsl(var(--accent-blue))]">{{ sounds: "Pronunciation", script: "Reading & writing", grammar: "Grammar" }[t.kind]}</span>}
  </div>
);

export default function LevelMap() {
  const { lang } = useParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [data, setData] = useState(null);
  const [level, setLevel] = useState(null);
  const [sel, setSel] = useState(null);
  const [saved, setSaved] = useState(new Set());
  const loadSaved = useCallback(() => savedLessonKeys().then(setSaved).catch(() => {}), []);
  useEffect(() => { loadSaved(); }, [loadSaved]);

  useEffect(() => {
    api.get(`/progress/${lang}`).then(({ data }) => {
      setData(data);
      setLevel(data.current_level);
      refreshUser();
    }).catch(() => navigate(`/languages?lang=${lang}`));
  }, [lang, navigate, refreshUser]);

  if (!data) return null;
  const lv = data.levels.find((l) => l.id === level);
  const nodes = [...lv.topics, { id: "final", title: `${lv.name} level test`, desc: "Pass to unlock the next level", status: lv.final_status }];
  const selected = nodes.find((n) => n.id === sel) || nodes.find((n) => n.status === "current");

  const pickLevel = async (l) => {
    if (!l.unlocked) return;
    setLevel(l.id);
    setSel(null);
    await api.post(`/progress/${lang}/level/${l.id}`).catch(() => {});
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 grid lg:grid-cols-12 gap-8">
      <aside className="lg:col-span-4 space-y-4">
        <div>
          <p className="caption">Your path</p>
          <h1 data-testid="level-map-title" className="font-display text-3xl font-extrabold text-slate-900">{lv.name} level</h1>
        </div>
        <div className="flex lg:flex-col gap-2 overflow-x-auto">
          {data.levels.map((l) => (
            <button
              key={l.id}
              data-testid={`level-tab-${l.id}`}
              onClick={() => pickLevel(l)}
              disabled={!l.unlocked}
              className={`text-left rounded-2xl border-2 p-4 min-w-[180px] transition-colors ${l.id === level ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary-light))]" : "border-slate-200 bg-white"} ${!l.unlocked ? "opacity-60" : ""}`}
            >
              <div className="flex items-center justify-between">
                <span className="font-display font-bold text-slate-900">{l.name}</span>
                {!l.unlocked ? <Lock size={16} className="text-slate-400" /> : l.passed ? <Trophy size={16} className="text-amber-500" /> : null}
              </div>
              <Progress value={(l.completed_count / l.topics.length) * 100} className="h-2 mt-3" />
              <p className="text-xs text-slate-500 mt-1.5">{l.completed_count}/{l.topics.length} topics</p>
            </button>
          ))}
        </div>
        <DownloadCard lang={lang} level={lv} onDone={loadSaved} />
        <button data-testid="map-tutor-button" onClick={() => navigate(`/tutor/${lang}`)} className="btn-ghost w-full"><MessageCircle size={18} /> Practise with AI tutor</button>
        <button data-testid="map-review-button" onClick={() => navigate(`/review/${lang}`)} className="btn-ghost w-full"><RotateCcw size={18} /> Review my mistakes</button>
      </aside>

      <section className="lg:col-span-5 flex flex-col items-center gap-10 py-6 rounded-[2rem] bg-white border border-slate-200 path-bg">
        {nodes.map((t, i) => (
          <TopicNode key={t.id} t={t} i={i} saved={saved.has(`${lang}|${level}|${t.id}`)} active={selected?.id === t.id} onClick={() => setSel(t.id)} />
        ))}
      </section>

      <aside className="lg:col-span-3">
        {selected && (
          <div key={selected.id} data-testid="topic-detail-card" className="lg:sticky lg:top-24 rounded-3xl bg-white border border-slate-200 p-6 animate-fade-up">
            <p className="caption">{selected.status === "completed" ? "Completed" : selected.status === "current" ? "Up next" : "Locked"}</p>
            <h2 className="font-display text-xl font-extrabold text-slate-900 mt-1">{selected.title}</h2>
            <p className="text-sm text-slate-600 mt-1">{selected.desc}</p>
            {selected.status !== "locked" && (
              <div className="flex flex-col gap-2 mt-5">
                {selected.id !== "final" && (
                  <button data-testid="start-lesson-button" onClick={() => navigate(`/lesson/${lang}/${level}/${selected.id}`)} className="btn-primary">
                    <BookOpen size={18} /> {selected.status === "completed" ? "Review lesson" : "Start lesson"}
                  </button>
                )}
                <button data-testid="start-assessment-button" onClick={() => navigate(`/assessment/${lang}/${level}/${selected.id}`)} className={selected.id === "final" ? "btn-primary" : "btn-ghost"}>
                  <Target size={18} /> {selected.id === "final" ? "Take level test" : selected.status === "completed" ? "Retake assessment" : "Take assessment"}
                </button>
              </div>
            )}
            {selected.status === "locked" && <p className="text-sm text-slate-500 mt-4">Pass the previous step with 70% or more to unlock.</p>}
          </div>
        )}
        {!selected && lv.passed && <p className="text-slate-600">Level complete. Amazing work!</p>}
      </aside>
    </div>
  );
}
